import { IconButton } from '@components/core/icon-button.component';
import { MainLayout } from '@components/layouts/main.layout';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useRequireAdmin } from '@hooks/use-require-admin.hook';
import { useAdminCategoriesQuery } from '@queries/admin-categories.query';
import { PlusIcon, RefreshCcwIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { AdminCategoriesPagination } from './components/admin-categories-pagination.component';
import { AdminCategoriesTable } from './components/admin-categories-table.component';
import { AdminCategoryCreateModal } from './components/admin-category-create-modal.component';
import { AdminCategoryModal } from './components/admin-category-modal.component';
import { useAdminCategoriesQueryParams } from './hooks/use-admin-categories-query-params.hook';

export const AdminCategoriesPage: React.FC = () => {
  const { isAdmin, isPending: isAdminPending } = useRequireAdmin();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const {
    DEFAULT_SORTING,
    handleLimitChange,
    page,
    queryParams,
    search,
    selectedCategoryId,
    setCategoryType,
    setPage,
    setSearch,
    setSelectedCategoryId,
    setSorting,
    sortingState,
    type,
  } = useAdminCategoriesQueryParams();

  const { data, isPending, isFetching, refetch: refetchCategories } = useAdminCategoriesQuery(queryParams);

  const categories = data?.categories ?? [];
  const pagination = data?.pagination;

  // Handle page bounds
  useEffect(() => {
    if (!pagination) return;

    const current = page;
    const total = pagination.totalPages ?? 0;

    if (total < 1) {
      if (current !== 1) void setPage(1);
      return;
    }

    if (current > total) {
      void setPage(Math.max(1, total));
    }
  }, [page, pagination, setPage]);

  if (isAdminPending || !isAdmin) {
    return null;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="Admin Categories" />
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="flex flex-col w-full gap-6">
          <Tile
            header={{
              title: 'Categories',
              actions: (
                <div className="flex items-center gap-2">
                  <IconButton
                    ariaLabel="Refresh categories"
                    disabled={isFetching}
                    icon={<RefreshCcwIcon />}
                    variant="secondary"
                    onClick={() => {
                      refetchCategories();
                    }}
                  />
                  <Button onClick={() => setIsCreateOpen(true)}>
                    <PlusIcon className="size-3 mr-1" />
                    New Category
                  </Button>
                </div>
              ),
            }}
          >
            <div className="flex flex-col gap-4">
              <AdminCategoriesTable
                categories={categories}
                categoryType={type}
                isLoading={isPending}
                onRowClick={(categoryId) => void setSelectedCategoryId(categoryId)}
                onSearchChange={(value) => {
                  void setSearch(value);
                  void setPage(1);
                }}
                onSortingChange={(state) => {
                  if (!state || state.length === 0) {
                    void setSorting(DEFAULT_SORTING.map((item) => ({ ...item })));
                    return;
                  }
                  void setSorting(state);
                }}
                onTypeChange={(value) => {
                  void setCategoryType(value);
                  void setPage(1);
                }}
                search={search}
                sorting={sortingState}
              />

              {pagination && (
                <AdminCategoriesPagination
                  isFetching={isFetching}
                  pagination={pagination}
                  onLimitChange={handleLimitChange}
                  onNextPage={() => void setPage(page + 1)}
                  onPreviousPage={() => void setPage(page - 1)}
                />
              )}
            </div>
          </Tile>
        </div>
      </MainLayout>

      <AdminCategoryCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      {selectedCategoryId && (
        <AdminCategoryModal
          categoryId={selectedCategoryId}
          isOpen
          onClose={() => void setSelectedCategoryId(null)}
        />
      )}
    </PageviewTracker>
  );
};
